import { Fragment, useEffect, useState } from "react";
import usePersistentFilters from "../Common/usePersistentFilters";
import { Badge, Button, Card, Col, Dropdown, Modal, Row, Table } from "react-bootstrap";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import swal from "sweetalert";
import api, { getFileUrl } from "../../../api";
import PageTitle from "../../layouts/PageTitle";
import AppPagination from "../Common/AppPagination";
import FilePreview from "../Common/FilePreview";
import PortalMenu from "../Common/PortalMenu";

const HandoverList = () => {
    const navigate = useNavigate();
    const [handovers, setHandovers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedHO, setSelectedHO] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [previewingPdf, setPreviewingPdf] = useState(false);
    const [searchParams] = useSearchParams();

    const workOrderFromUrl = searchParams.get("workOrder") || "";
    const projectFromUrl = searchParams.get("project") || "";
    // Pagination
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        limit: 10,
    });

    const [filters, setFilters] = usePersistentFilters("handover_list_filters", {
        search: "",
        status: "",
        project: projectFromUrl || "",
        workOrder: workOrderFromUrl || "",
    });

    const [workOrders, setWorkOrders] = useState([]);

    useEffect(() => {
        fetchHandovers();
        fetchWorkOrders();
    }, [pagination.currentPage, filters, workOrderFromUrl, projectFromUrl]);

    // ================================
    // FETCH HANDOVER DATA
    // ================================
    const handlePageChange = (page) => {
        setPagination(prev => ({ ...prev, currentPage: page }));
    };

    const fetchHandovers = async () => {
        try {
            setLoading(true);

            const params = {
                page: pagination.currentPage,
                limit: pagination.limit,
                ...(filters.search && { search: filters.search }),
                ...(filters.status && { status: filters.status }),
                ...(filters.project && { project: filters.project }),
                ...(filters.workOrder && { workOrder: filters.workOrder }),
            };

            const res = await api.get("/handover", { params });

            if (res.data.success) {
                setHandovers(res.data.data || []);
                if (res.data.pagination) {
                    setPagination((prev) => ({
                        ...prev,
                        totalPages: res.data.pagination.totalPages,
                        totalCount: res.data.pagination.totalCount,
                        currentPage: res.data.pagination.currentPage,
                    }));
                }
            }
        } catch (error) {
            swal("Error", "Failed to load handovers", "error");
        } finally {
            setLoading(false);
        }
    };

    // ================================
    // FETCH PROJECT LIST
    // ================================
    const fetchWorkOrders = async () => {
        try {
            const res = await api.get("/work-orders?limit=1000");
            if (res.data.success) {
                setWorkOrders(res.data.data || []);
            }
        } catch (error) {
            console.error("Error fetching work orders:", error);
        }
    };

    const handlePreviewPDF = async (id) => {
        try {
            setPreviewingPdf(true);

            const res = await api.get(`/handover/${id}/pdf`, {
                responseType: "blob",
            });

            const fileURL = window.URL.createObjectURL(
                new Blob([res.data], { type: "application/pdf" }),
            );

            window.open(fileURL, "_blank");
        } catch (err) {
            console.error("Handover PDF preview error:", err);
            swal("Error", "Failed to preview Handover PDF", "error");
        } finally {
            setPreviewingPdf(false);
        }
    };

    const handleDownloadPDF = async (id) => {
        try {
            const res = await api.get(`/handover/${id}/pdf`, {
                responseType: "blob",
            });

            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement("a");
            link.href = url;
            link.download = `Handover-${id}.pdf`;
            link.click();

            swal("Success", "Handover PDF downloaded", "success");
        } catch (err) {
            swal("Error", "Failed to download Handover PDF", "error");
        }
    };

    // ================================
    // DELETE HANDOVER
    // ================================
    const handleDelete = async (id) => {
        const confirm = await swal({
            title: "Delete?",
            text: "This handover record will be permanently deleted.",
            icon: "warning",
            buttons: true,
            dangerMode: true,
        });

        if (!confirm) return;

        try {
            const res = await api.delete(`/handover/${id}`);
            if (res.data.success) {
                swal("Deleted!", "Handover deleted successfully", "success");
                fetchHandovers();
            }
        } catch (error) {
            swal(
                "Error",
                error.response?.data?.message || "Failed to delete",
                "error",
            );
        }
    };

    // ================================
    // VIEW DETAILS
    // ================================
    const handleView = async (handover) => {
        try {
            const res = await api.get(`/handover/${handover._id}`);
            if (res.data.success) {
                setSelectedHO(res.data.data);
                setShowModal(true);
            }
        } catch (error) {
            swal("Error", "Failed to fetch handover details", "error");
        }
    };

    // ================================
    // FILTER HANDLERS
    // ================================
    const handleFilterChange = (name, value) => {
        setFilters((prev) => ({ ...prev, [name]: value }));
        setPagination((prev) => ({ ...prev, currentPage: 1 }));
    };

    const getStatusBadge = (status) => {
        const map = {
            Pending: "warning",
            Approved: "success",
            Rejected: "danger",
        };
        return map[status] || "secondary";
    };

    const svgMenu = (
        <svg width="20px" height="20px" viewBox="0 0 24 24">
            <circle fill="#000000" cx="5" cy="12" r="2"></circle>
            <circle fill="#000000" cx="12" cy="12" r="2"></circle>
            <circle fill="#000000" cx="19" cy="12" r="2"></circle>
        </svg>
    );

    return (
        <Fragment>
            <PageTitle activeMenu="Handover" motherMenu="ITC" />

            {/* TABLE */}
            <Row>
                <Col lg={12}>
                    <Card>
                        <Card.Header className="d-flex justify-content-between">
                            <Card.Title>Handover List</Card.Title>
                            <Link
                                to={
                                    workOrderFromUrl
                                        ? `/handover/add?${projectFromUrl ? `project=${projectFromUrl}&` : ""}workOrder=${workOrderFromUrl}`
                                        : "/handover/add"
                                }
                                className="btn btn-primary"
                            >
                                <i className="fas fa-plus me-2"></i> Add Handover
                            </Link>
                        </Card.Header>

                        <Card.Body>
                            {loading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary"></div>
                                </div>
                            ) : handovers.length === 0 ? (
                                <div className="text-center py-5">
                                    <i className="fas fa-folder-open fa-3x text-muted mb-2"></i>
                                    <h5>No Handover Found</h5>
                                    <p className="text-muted">Add your first handover record.</p>
                                    <Link
                                        to={
                                            workOrderFromUrl
                                                ? `/handover/add?${projectFromUrl ? `project=${projectFromUrl}&` : ""}workOrder=${workOrderFromUrl}`
                                                : "/handover/add"
                                        }
                                        className="btn btn-primary"
                                    >
                                        Add Handover
                                    </Link>
                                </div>
                            ) : (
                                <>
                                    <div className="table-responsive">
                                        <Table hover>
                                            <thead>
                                                <tr>
                                                    <th>#</th>
                                                    <th>HO Number</th>
                                                    <th>Work Order</th>
                                                    <th>Status</th>
                                                    <th>BOQ Items</th>
                                                    <th>Checklist</th>
                                                    <th>Date</th>
                                                    <th>Submitted By</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>

                                            <tbody>
                                                {handovers.map((h, i) => (
                                                    <tr key={h._id}>
                                                        <td>
                                                            {(pagination.currentPage - 1) * pagination.limit +
                                                                i +
                                                                1}
                                                        </td>

                                                        <td>
                                                            <strong>{h.hoNumber}</strong>
                                                        </td>

                                                        <td>
                                                            {h.workOrder?.woNo || h.workOrder?.woNumber || "—"}
                                                        </td>

                                                        <td>
                                                            <Badge bg={getStatusBadge(h.status)}>
                                                                {h.status}
                                                            </Badge>
                                                        </td>

                                                        <td>{h.boqSelected?.length || 0}</td>
                                                        <td>{h.checklist?.length || 0}</td>

                                                        <td>
                                                            {h.createdAt
                                                                ? new Date(h.createdAt).toLocaleDateString()
                                                                : "N/A"}
                                                        </td>

                                                        <td>{h.submittedBy?.name || "N/A"}</td>

                                                        <td>
                                                            <Dropdown>
                                                                <Dropdown.Toggle
                                                                    variant=""
                                                                    as="div"
                                                                    className="btn-link c-pointer i-false"
                                                                >
                                                                    {svgMenu}
                                                                </Dropdown.Toggle>

                                                                <Dropdown.Menu align="end" as={PortalMenu}>
                                                                    <Dropdown.Item onClick={() => handleView(h)}>
                                                                        <i className="fas fa-eye me-2"></i>
                                                                        View Details
                                                                    </Dropdown.Item>

                                                                    {h.handoverDocuments?.length > 0 && (
                                                                        <>
                                                                            <Dropdown.Divider />
                                                                            <h6 className="dropdown-header">Documents</h6>
                                                                            {h.handoverDocuments.map((doc, idx) => (
                                                                                <Dropdown.Item
                                                                                    key={idx}
                                                                                    href={getFileUrl(doc.fileUrl)}
                                                                                    target="_blank"
                                                                                    rel="noopener noreferrer"
                                                                                >
                                                                                    <i className="fas fa-file-alt me-2 text-info"></i>
                                                                                    {doc.fileName || `Document ${idx + 1}`}
                                                                                </Dropdown.Item>
                                                                            ))}
                                                                        </>
                                                                    )}

                                                                    {h.clientSignature?.fileUrl && (
                                                                        <Dropdown.Item
                                                                            href={getFileUrl(h.clientSignature.fileUrl)}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                        >
                                                                            <i className="fas fa-file-signature me-2 text-warning"></i>
                                                                            Client Signature
                                                                        </Dropdown.Item>
                                                                    )}

                                                                    <Dropdown.Divider />

                                                                    <Dropdown.Item
                                                                        onClick={() =>
                                                                            navigate(
                                                                                workOrderFromUrl
                                                                                    ? `/handover/edit/${h._id}?${projectFromUrl ? `project=${projectFromUrl}&` : ""}workOrder=${workOrderFromUrl}`
                                                                                    : `/handover/edit/${h._id}`
                                                                            )
                                                                        }
                                                                    >
                                                                        <i className="fas fa-edit me-2"></i>
                                                                        Edit
                                                                    </Dropdown.Item>
                                                                    <Dropdown.Item
                                                                        onClick={() => handlePreviewPDF(h._id)}
                                                                        disabled={previewingPdf}
                                                                    >
                                                                        <i className="fas fa-eye me-2 text-primary"></i>
                                                                        Preview PDF
                                                                    </Dropdown.Item>
                                                                    <Dropdown.Item
                                                                        onClick={() => handleDownloadPDF(h._id)}
                                                                    >
                                                                        <i className="fas fa-file-pdf me-2 text-danger"></i>
                                                                        Download PDF
                                                                    </Dropdown.Item>

                                                                    <Dropdown.Item
                                                                        className="text-danger"
                                                                        onClick={() => handleDelete(h._id)}
                                                                    >
                                                                        <i className="fas fa-trash me-2"></i>
                                                                        Delete
                                                                    </Dropdown.Item>
                                                                </Dropdown.Menu>
                                                            </Dropdown>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>

                                    {/* Pagination */}
                                    <AppPagination
                                        currentPage={pagination.currentPage}
                                        totalPages={pagination.totalPages}
                                        totalCount={pagination.totalCount}
                                        itemsLength={handovers.length}
                                        limit={pagination.limit}
                                        onPageChange={handlePageChange}
                                    />
                                </>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* ================================
 VIEW MODAL
 ================================ */}
            {selectedHO && (
                <Modal
                    show={showModal}
                    onHide={() => setShowModal(false)}
                    size="lg"
                    centered
                >
                    <Modal.Header closeButton>
                        <Modal.Title>Handover Details</Modal.Title>
                    </Modal.Header>

                    <Modal.Body>
                        {/* Basic Info */}
                        <Row className="mb-3">
                            <Col md={6}>
                                <p>
                                    <strong>HO Number:</strong> {selectedHO.hoNumber}
                                </p>
                            </Col>
                            <Col md={6}>
                                <p>
                                    <strong>Status:</strong>{" "}
                                    <Badge bg={getStatusBadge(selectedHO.status)}>
                                        {selectedHO.status}
                                    </Badge>
                                </p>
                            </Col>
                        </Row>

                        <Row className="mb-3">
                            <Col md={6}>
                                <p>
                                    <strong>Work Order:</strong> {selectedHO.workOrder?.woNo} —{" "}
                                    {selectedHO.workOrder?.scope}
                                </p>
                            </Col>
                            <Col md={6}>
                                <p>
                                    <strong>Date Submitted:</strong>{" "}
                                    {selectedHO.createdAt
                                        ? new Date(selectedHO.createdAt).toLocaleDateString()
                                        : "N/A"}
                                </p>
                            </Col>
                        </Row>

                        {/* ===================
 BOQ Items
 =================== */}
                        {selectedHO.boqSelected && selectedHO.boqSelected.length > 0 && (
                            <div className="mb-3">
                                <h5>BOQ Items</h5>
                                <Table bordered size="sm">
                                    <thead>
                                        <tr>
                                            <th>Item</th>
                                            <th>Description</th>
                                            <th>Qty</th>
                                            <th>Unit</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedHO.boqSelected.map((boq, index) => {
                                            const itemName = boq.itemName || boq.boqName || "-";
                                            const qty =
                                                boq.quantity !== undefined
                                                    ? boq.quantity
                                                    : boq.qty !== undefined
                                                        ? boq.qty
                                                        : "-";
                                            const unit = boq.unit || "-";

                                            return (
                                                <tr key={index}>
                                                    <td>{itemName}</td>
                                                    <td>{boq.description || "-"}</td>
                                                    <td>{qty}</td>
                                                    <td>{unit}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </div>
                        )}

                        {/* Checklist */}
                        {selectedHO.checklist?.length > 0 && (
                            <div className="mb-3">
                                <h5>Checklist</h5>
                                <Table bordered size="sm">
                                    <thead>
                                        <tr>
                                            <th>Item</th>
                                            <th>Status</th>
                                            <th>Remarks</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedHO.checklist.map((item, index) => (
                                            <tr key={index}>
                                                <td>{item.item}</td>
                                                <td>
                                                    <Badge bg={getStatusBadge(item.status)}>
                                                        {item.status}
                                                    </Badge>
                                                </td>
                                                <td>{item.remarks || "-"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        )}

                        {/* Documents */}
                        {(() => {
                            const docs = Array.isArray(selectedHO.handoverDocuments)
                                ? selectedHO.handoverDocuments
                                : selectedHO.handoverDocuments
                                    ? [selectedHO.handoverDocuments]
                                    : [];

                            return (
                                docs.length > 0 && (
                                    <div className="mb-3">
                                        <h5>Handover Documents</h5>
                                        <div className="d-flex flex-column gap-2">
                                            {docs.map((doc, index) => (
                                                <FilePreview
                                                    key={index}
                                                    file={{
                                                        fileName: doc.fileName,
                                                        fileUrl: doc.fileUrl
                                                    }}
                                                    onRemove={async () => {
                                                        const confirm = await swal({
                                                            title: "Delete Document?",
                                                            text: "This will remove the document file.",
                                                            icon: "warning",
                                                            buttons: ["Cancel", "Delete"],
                                                            dangerMode: true,
                                                        });
                                                        if (!confirm) return;
                                                        try {
                                                            const res = await api.patch(`/handover/${selectedHO._id}/remove-file`, {
                                                                field: 'handoverDocuments',
                                                                index: index
                                                            });
                                                            if (res.data.success) {
                                                                swal("Deleted!", "Document removed", "success");
                                                                handleView(selectedHO); // Refresh modal
                                                            }
                                                        } catch (err) {
                                                            swal("Error", "Failed to remove document", "error");
                                                        }
                                                    }}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                )
                            );
                        })()}

                        {/* Client Signature */}
                        {selectedHO.clientSignature?.fileUrl && (
                            <div className="mb-3">
                                <h5>Client Signature</h5>
                                <FilePreview
                                    file={{
                                        fileName: selectedHO.clientSignature.fileName || "Signature",
                                        fileUrl: selectedHO.clientSignature.fileUrl
                                    }}
                                    onRemove={async () => {
                                        const confirm = await swal({
                                            title: "Delete Signature?",
                                            text: "This will remove the client signature file.",
                                            icon: "warning",
                                            buttons: ["Cancel", "Delete"],
                                            dangerMode: true,
                                        });
                                        if (!confirm) return;
                                        try {
                                            const res = await api.patch(`/handover/${selectedHO._id}/remove-file`, {
                                                field: 'clientSignature'
                                            });
                                            if (res.data.success) {
                                                swal("Deleted!", "Signature removed", "success");
                                                handleView(selectedHO); // Refresh modal
                                            }
                                        } catch (err) {
                                            swal("Error", "Failed to remove signature", "error");
                                        }
                                    }}
                                />
                            </div>
                        )}
                    </Modal.Body>

                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowModal(false)}>
                            Close
                        </Button>
                    </Modal.Footer>
                </Modal>
            )}
            {/* =========================
 PDF PREVIEW LOADER
========================== */}
            {previewingPdf && (
                <div
                    className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                    style={{
                        background: "rgba(0,0,0,0.4)",
                        zIndex: 9999,
                    }}
                >
                    <div className="bg-white p-4 rounded shadow text-center">
                        <div className="spinner-border text-primary mb-3"></div>
                        <h6 className="mb-0">Preparing Handover PDF…</h6>
                    </div>
                </div>
            )}
        </Fragment>
    );
};

export default HandoverList;
